import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LicoMedsLogoTransparent from "../../assets/images/licomeds-logo-transparent.png";
import { useTheme } from "../../contexts/ThemeContext";
import { ROUTES, TEXT_SNIPPETS } from "../../constants/routes";
import styles from "./Navbar.module.css";

interface NavbarProps {
  className?: string;
}

const Navbar: React.FC<NavbarProps> = ({ className }) => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsNavOpen(false);
    setOpenDropdown(null);

    if (!location.hash) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const navbar = document.querySelector(`.${styles.mobileNavCollapse}`);
      const toggler = document.querySelector(`.${styles.navbarToggler}`);
      const themeToggle = document.querySelector(`.${styles.themeToggle}`);

      if (
        navbar &&
        !navbar.contains(event.target as Node) &&
        toggler &&
        !toggler.contains(event.target as Node) &&
        themeToggle &&
        !themeToggle.contains(event.target as Node)
      ) {
        setIsNavOpen(false);
        setOpenDropdown(null);
      }
    };

    if (isNavOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setOpenDropdown(null);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isNavOpen]);

  const toggleDropdown = (dropdownName: string) => {
    if (window.innerWidth <= 991) {
      setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    }
  };

  const isActiveRoute = (path: string): boolean => {
    if (path === ROUTES.HOME.path) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <ul className={mobile ? styles.mobileNavList : styles.desktopNavList}>
      <li className={styles.navItem}>
        <Link
          to={ROUTES.HOME.path}
          className={`${styles.navLink} ${
            isActiveRoute(ROUTES.HOME.path) ? styles.active : ""
          }`}
        >
          <i className="fas fa-home"></i>
          <span>Home</span>
        </Link>
      </li>

      <li className={styles.navItem}>
        <Link
          to={ROUTES.ABOUT.path}
          className={`${styles.navLink} ${
            isActiveRoute(ROUTES.ABOUT.path) ? styles.active : ""
          }`}
        >
          <i className="fas fa-info-circle"></i>
          <span>About</span>
        </Link>
      </li>

      <li className={styles.navItem}>
        <Link
          to={ROUTES.FEATURES.path}
          className={`${styles.navLink} ${
            isActiveRoute(ROUTES.FEATURES.path) ? styles.active : ""
          }`}
        >
          <i className="fas fa-star"></i>
          <span>Features</span>
        </Link>
      </li>

      <li className={styles.navItem}>
        <Link
          to={ROUTES.DONATE.path}
          className={`${styles.navLink} ${
            isActiveRoute(ROUTES.DONATE.path) ? styles.active : ""
          }`}
        >
          <i className="fas fa-heart"></i>
          <span>Donate</span>
        </Link>
      </li>

      <li
        className={`${styles.navItem} ${styles.dropdown} ${
          openDropdown === "support" ? styles.open : ""
        }`}
      >
        <button
          className={`${styles.navLink} ${styles.dropdownToggle}`}
          type="button"
          onClick={() => toggleDropdown("support")}
          aria-expanded={openDropdown === "support"}
          aria-haspopup="true"
        >
          <i className="fas fa-question-circle"></i>
          <span>Support</span>
        </button>
        <ul className={styles.dropdownMenu}>
          <li>
            <Link
              to={ROUTES.BLOG.path}
              className={`${styles.dropdownItem} ${
                isActiveRoute(ROUTES.BLOG.path) ? styles.active : ""
              }`}
              onClick={() => {
                setIsNavOpen(false);
                setOpenDropdown(null);
              }}
            >
              <i className="fas fa-blog"></i>
              <span>Blog</span>
            </Link>
          </li>
          <li>
            <Link
              to={ROUTES.FAQ.path}
              className={`${styles.dropdownItem} ${
                isActiveRoute(ROUTES.FAQ.path) ? styles.active : ""
              }`}
              onClick={() => {
                setIsNavOpen(false);
                setOpenDropdown(null);
              }}
            >
              <i className="fas fa-question-circle"></i>
              <span>FAQ</span>
            </Link>
          </li>
          <li>
            <Link
              to={ROUTES.CONTACTUS.path}
              className={`${styles.dropdownItem} ${
                isActiveRoute(ROUTES.CONTACTUS.path) ? styles.active : ""
              }`}
              onClick={() => {
                setIsNavOpen(false);
                setOpenDropdown(null);
              }}
            >
              <i className="fas fa-envelope"></i>
              <span>Contact Us</span>
            </Link>
          </li>
          <li>
            <Link
              to={ROUTES.TERMS.path}
              className={`${styles.dropdownItem} ${
                isActiveRoute(ROUTES.TERMS.path) ? styles.active : ""
              }`}
              onClick={() => {
                setIsNavOpen(false);
                setOpenDropdown(null);
              }}
            >
              <i className="fas fa-gavel"></i>
              <span>Terms & Conditions</span>
            </Link>
          </li>
        </ul>
      </li>
    </ul>
  );

  return (
    <>
      <nav
        className={`${styles.navbar} ${isScrolled ? styles.scrolled : ""} ${
          className || ""
        }`}
      >
        <div className={styles.container}>
          {/* Brand */}
          <Link to={ROUTES.HOME.path} className={styles.navbarBrand}>
            <div className={`${styles.brandIcon} ${styles.bounceGentle}`}>
              <img
                src={LicoMedsLogoTransparent}
                alt={ROUTES.COMPANY.NAME}
                className={styles.faviconIcon}
              />
            </div>
            <div className={styles.brandContent}>
              <div className={styles.brandLayout}>
                <div className={styles.brandNameStacked}>
                  <div className={styles.brandPrimary}>Lico</div>
                  <div className={styles.brandAccent}>Meds</div>
                </div>
                <div className={styles.brandTaglineStacked}>
                  {ROUTES.COMPANY.TAGLINE}
                </div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className={styles.desktopNav}>
            <NavLinks mobile={false} />
          </div>

          {/* Right side items */}
          <div className={styles.rightItems}>
            {/* Theme Toggle */}
            <button
              className={styles.themeToggle}
              onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
              }}
              title="Toggle theme"
              aria-label="Toggle theme"
            >
              <i
                className={theme === "dark" ? "fas fa-sun" : "fas fa-moon"}
              ></i>
            </button>

            {/* Mobile toggler */}
            <button
              className={`${styles.navbarToggler} ${
                isNavOpen ? styles.active : ""
              }`}
              type="button"
              onClick={() => setIsNavOpen(!isNavOpen)}
              aria-expanded={isNavOpen}
              aria-label="Toggle navigation"
            >
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
            </button>
          </div>

          {/* Mobile Navigation */}
          <div
            className={`${styles.mobileNavCollapse} ${
              isNavOpen ? styles.show : ""
            }`}
          >
            <NavLinks mobile={true} />
          </div>
        </div>
      </nav>

      {/* 24/7 Support Badge */}
      <div className={styles.supportIndicator}>
        <i className="fas fa-clock"></i>
        <span>{TEXT_SNIPPETS.SUPPORT_BADGE}</span>
      </div>
    </>
  );
};

export default Navbar;
